'use client';

import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCookieValue } from '../../App';
import { useAppDispatch } from '../../app/hooks';
import type { RootState } from '../../app/store';
import type { MembersType } from '../../assets/dummy';
import BarchartComp from '../../components/barChart/BarChart';
import Button from '../../components/button/Button';
import HistoryRouting from '../../components/HistoryRouting';
import type { FilterByDateWithFlag } from '../../components/lineChart/LineChart';
import Spinner from '../../components/spinner/Spinner';
import Table from '../../components/table/Table';
import {
  GetCardEnrollment,
  GetGraphMemberCounts,
  GetGraphNewMembersCounts,
} from '../../features/partnerSlice';
import useExportCSV from '../../hooks/useExportCSV';
import { useLocation } from 'react-router';
import { formatDate } from '../../assets/helper';

const memberDetailsConfig = {
  headers: [
    { key: 'joinedDate', label: 'Joined Date' },
    { key: 'fullName', label: 'Full Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'count', label: 'Cards Count' },
    { key: 'firstEnrolledCard', label: 'First Enrolled Card' },
   /* { key: 'qualifiedStatus', label: 'Qualified Status' },*/
  ],
};

const TotalCustomers = () => {
  const location = useLocation();
  const incomingTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState<'1' | '2' | '3' | '4'>(
    incomingTab
  );
  const [users, setUsers] = useState<MembersType[]>([]);
  const handleExportUsers = useExportCSV();
  const [isLoading, setIsLoading] = useState(false);
  const [customDate, setCustomDate] = useState<FilterByDateWithFlag>({
    startDate: '',
    endDate: '',
    typeName: '',
  });
  const {
    graphMemberCounts,
    graphNewMembersCounts,
    cardEnrollment,
    dashboardInfo,
    loadingStates,
  } = useSelector((state: RootState) => state.partner);
  const dispatch = useAppDispatch();

  const fetchMemberDetails = useCallback(
    async (isExporting: boolean = false) => {
      try {
        if (!isExporting) setIsLoading(true);

        const token = getCookieValue('token');
        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/usr/u/g/p/u`,
          isExporting
            ? {
                exportToCsv: true,
              }
            : {},
          {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        if (!isExporting) {
          const formattedData = response.data.map((user: any) => ({
            ...user,
            joinedDate: formatDate(user.joinedDate),
            firstEnrolledCard: user.firstEnrolledCard
              ? formatDate(user.firstEnrolledCard)
              : null,
          }));
          setUsers(formattedData);
        }

        if (isExporting)
          handleExportUsers(
            memberDetailsConfig,
            response.data,
            'Member Details'
          );
      } catch (err) {
        console.log('err: ', err);
      } finally {
        if (!isExporting) setIsLoading(false);
      }
    },
    [handleExportUsers]
  );

  useEffect(() => {
    const { typeName, ...date } = customDate;

    if (typeName === 'MembersCount') {
      dispatch(GetGraphMemberCounts(date));
    } else if (!graphMemberCounts && activeTab === '1') {
      dispatch(GetGraphMemberCounts());
    }
    if (typeName === 'NewMembers') {
      dispatch(GetGraphNewMembersCounts(date));
    } else if (!graphNewMembersCounts && activeTab === '2') {
      dispatch(GetGraphNewMembersCounts());
    }

    if (typeName === 'MemberCardEnrollment') {
      dispatch(GetCardEnrollment(date));
    } else if (!cardEnrollment && activeTab === '3') {
      dispatch(GetCardEnrollment());
    }
    if (typeName) setCustomDate({ ...customDate, typeName: '' });
  }, [
    activeTab,
    customDate,
    cardEnrollment,
    graphMemberCounts,
    graphNewMembersCounts,
    dispatch,
  ]);

  useEffect(() => {
    if (activeTab === '4') {
      fetchMemberDetails();
    }
  }, [activeTab, fetchMemberDetails]);

  return (
    <div className='mb-8'>
      {/* Toggle Button */}
      <HistoryRouting history='Reporting/Members Data' />
      <div className='space-y-10'>
        <div className='space-y-6 border border-gray-200 primary-shadow p-4'>
          <p>
            {/* <span className='font-medium text-2xl'>Reporting / </span> */}
            <span className='font-bold text-2xl'>Total Members to Date</span>
          </p>
          {/* <p className='text-xl font-semibold'>5,972</p> */}
          {loadingStates['GetDashboardInfo'] ? (
            <Spinner />
          ) : (
            <p className='text-xl font-semibold'>{dashboardInfo?.userCount}</p>
          )}
        </div>
        <div className='border border-gray-200 shadow px-7 pt-7 pb-5'>
          <div className='grid gap-5 xl:gap-10 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'>
            <Button
              className={` ${
                activeTab === '1'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }`}
              onClick={() => setActiveTab('1')}
            >
              Member Count
            </Button>
            <Button
              className={` ${
                activeTab === '2'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }`}
              onClick={() => setActiveTab('2')}
            >
              New Members
            </Button>
            <Button
              className={`${
                activeTab === '3'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }`}
              onClick={() => setActiveTab('3')}
            >
              Card Enrollment
            </Button>
            <Button
              className={`${
                activeTab === '4'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }`}
              onClick={() => setActiveTab('4')}
            >
              Member Details
            </Button>
          </div>
          {/* Display Charts or List View */}
          <div className='grid grid-cols-1 '>
            {activeTab === '1' && (
              <BarchartComp
                data={graphMemberCounts}
                isFiltering={true}
                chartName='MembersCount'
                setCustomDate={setCustomDate}
                isLoading={loadingStates['GetGraphMemberCounts']}
                description='Active vs. Inactive Members (Active members are those that have transacted in the respective month)'
                showExport
              />
            )}
            {activeTab === '2' && (
              <BarchartComp
                data={graphNewMembersCounts}
                dataKey1='newCustomers'
                chartName='NewMembers'
                name1='New Members'
                isOneColumn={true}
                setCustomDate={setCustomDate}
                isFiltering={true}
                isLoading={loadingStates['GetGraphNewMembersCounts']}
                description=' Monthly New Members Joining the Program'
                showExport
              />
            )}
            {activeTab === '3' && (
              <BarchartComp
                data={cardEnrollment}
                dataKey1='customerWithCard'
                dataKey2='customerNoCard'
                chartName='MemberCardEnrollment'
                name1='Member with at-least 1 Card'
                name2='Member without any Card'
                setCustomDate={setCustomDate}
                isFiltering={true}
                isLoading={loadingStates['GetCardEnrollment']}
                description='Member with at-least 1 enrolled card vs. members without any enrolled cards'
                showExport
              />
            )}
            {activeTab === '4' && (
              <div className={users.length ? 'mt-10 mb-4 w-full' : ''}>
                {isLoading ? (
                  <div className='my-12 w-full flex justify-center'>
                    <Spinner />
                  </div>
                ) : users.length ? (
                  <>
                    <p className='text-sm  pb-5'>
                      View the 100 most recent member enrollments here. For a
                      full list of members, please use the export feature.
                    </p>
                    <Table
                      data={users}
                      config={memberDetailsConfig}
                      showExportButton={true}
                      width='w-full'
                      enableSorting={false}
                      title='Member Details'
                      customCSVExporting={fetchMemberDetails}
                    />
                  </>
                ) : (
                  <p className='italic text-gray-600 text-center mt-10'>
                    No data available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalCustomers;
